import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FlexGrid } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import uniqid from 'uniqid';
import styles from './StationHours.module.css';

const cx = classnames.bind(styles);

export default function StationHours({ hours, isHoursOpen, onChange }) {
  // See https://github.com/opening-hours/opening_hours.js for datetime formats
  const is24Hours = hours.status === '24/7';
  const nextInterval = hours && hours.next_intervals[0];
  const { open: nextOpen, close: nextClose } = nextInterval || {};
  const stationHoursEl = useRef();

  const isOpen = useMemo(() => {
    if (!nextOpen || !nextClose) {
      return is24Hours;
    }
    const currently = new Date();
    const now = currently.getTime();
    const open = new Date(nextOpen).getTime();
    const close = new Date(nextClose).getTime();

    return !!(open <= now && now < close);
  }, [is24Hours, nextClose, nextOpen]);

  const handleToggleClick = useCallback(() => {
    onChange(!isHoursOpen);
  }, [isHoursOpen, onChange]);

  useEffect(() => {
    function handleClickOutside(event) {
      const el = stationHoursEl.current;

      if (el && !el.contains(event.target) && !!isHoursOpen) {
        onChange(!isHoursOpen);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isHoursOpen, onChange, stationHoursEl]);

  if (is24Hours) {
    return (
      <span className={cx('statusOpen')}>Open 24 Hours</span>
    );
  }

  if (!nextInterval) {
    return (<span />);
  }

  const formatDate = date => ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][(new Date(date)).getDay()];
  const formatTime = date => (new Date(date)).toLocaleTimeString('en-us', { hour: '2-digit', minute: '2-digit' }).replace(' ', '');

  // Today's hours or tomorrow's opening time
  const nextIntervalText = isOpen
    ? `${formatTime(nextInterval.open)}-${formatTime(nextInterval.close)}`
    : `Open at ${formatTime(nextInterval.open)}`;

  const icon = isHoursOpen ? faChevronUp : faChevronDown;

  // Popup content with hours for the upcoming days
  const HoursNode = () => (
    <FlexGrid className={cx('hoursPopUp')}>
      {hours.next_intervals.map(interval => ([
        (
          <FlexGrid.Column key={uniqid()} mobile={5}>{formatDate(interval.open)}</FlexGrid.Column>
        ),
        (
          <FlexGrid.Column key={uniqid()} mobile={7}>
            {`${formatTime(interval.open)} - ${formatTime(interval.close)}`}
          </FlexGrid.Column>
        ),
      ]))}
    </FlexGrid>
  );

  return (
    <div ref={stationHoursEl}>
      <span className={isOpen ? cx('statusOpen') : cx('statusClosed')}>
        {`${isOpen ? 'Open' : 'Closed'} Now: `}
      </span>
      <span>
        {nextIntervalText}
        <FontAwesomeIcon
          icon={icon}
          style={{ cursor: 'pointer', marginLeft: '5' }}
          onClick={handleToggleClick}
        />
      </span>
      {isHoursOpen && (
        <HoursNode />
      )}
    </div>
  );
}

StationHours.propTypes = {
  // eslint-disable-next-line react/require-default-props
  isHoursOpen: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/require-default-props
  onChange: PropTypes.func.isRequired,
  hours: PropTypes.shape({
    opening_hours: PropTypes.string,
    next_intervals: PropTypes.arrayOf(PropTypes.object),
    status: PropTypes.string,
  }),
};

StationHours.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  isHoursOpen: false,
  // eslint-disable-next-line react/default-props-match-prop-types
  onChange: () => { },
  hours: {},
};
